import { useEffect } from 'react';
import { useSiteI18n } from './SiteI18n';

interface SiteSEOProps {
  title: string;
  description: string;
  path: string; // e.g. /site or /site/platforma
  image?: string;
  jsonLd?: Record<string, any> | Record<string, any>[];
  type?: 'website' | 'article';
}

const BASE_URL = 'https://aetheris-is.com';

const upsert = (selector: string, create: () => HTMLElement, apply: (el: HTMLElement) => void) => {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) { el = create(); document.head.appendChild(el); }
  apply(el);
  return el;
};

const meta = (name: string, content: string, attr: 'name' | 'property' = 'name') => {
  upsert(`meta[${attr}="${name}"]`, () => {
    const m = document.createElement('meta'); m.setAttribute(attr, name); return m;
  }, (m) => m.setAttribute('content', content));
};

export const SiteSEO: React.FC<SiteSEOProps> = ({ title, description, path, image, jsonLd, type = 'website' }) => {
  const { lang } = useSiteI18n();
  const url = `${BASE_URL}${path}`;
  const img = image || `${BASE_URL}/lovable-uploads/aetheris-og.jpg`;

  useEffect(() => {
    document.title = title;
    document.documentElement.lang = lang;

    meta('description', description);
    meta('robots', 'index, follow, max-image-preview:large, max-snippet:-1');
    meta('og:title', title, 'property');
    meta('og:description', description, 'property');
    meta('og:type', type, 'property');
    meta('og:url', url, 'property');
    meta('og:image', img, 'property');
    meta('og:locale', lang === 'ro' ? 'ro_RO' : 'en_US', 'property');
    meta('twitter:card', 'summary_large_image');
    meta('twitter:title', title);
    meta('twitter:description', description);
    meta('twitter:image', img);

    upsert('link[rel="canonical"]', () => {
      const l = document.createElement('link'); l.setAttribute('rel', 'canonical'); return l;
    }, (l) => l.setAttribute('href', url));

    // hreflang
    const langs: Array<{ code: string; href: string }> = [
      { code: 'ro', href: `${BASE_URL}${path}` },
      { code: 'en', href: `${BASE_URL}${path}` },
      { code: 'x-default', href: `${BASE_URL}${path}` },
    ];
    document.head.querySelectorAll('link[data-hreflang="1"]').forEach((n) => n.remove());
    langs.forEach(({ code, href }) => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'alternate');
      l.setAttribute('hreflang', code);
      l.setAttribute('href', href);
      l.setAttribute('data-hreflang', '1');
      document.head.appendChild(l);
    });

    // JSON-LD
    document.head.querySelectorAll('script[data-jsonld="site"]').forEach((n) => n.remove());
    const ldArr = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    const orgLd = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'AETHERIS',
      url: BASE_URL,
      logo: `${BASE_URL}/favicon.png`,
      sameAs: ['https://aether-is.com'],
      contactPoint: [{
        '@type': 'ContactPoint',
        email: 'hello@aetheris-is.com',
        telephone: '+40-752-488-258',
        contactType: 'customer support',
        areaServed: 'EU',
        availableLanguage: ['Romanian', 'English'],
      }],
    };

    // Auto BreadcrumbList from path
    const segments = path.split('/').filter(Boolean);
    const crumbs: Array<{ name: string; url: string }> = [{ name: 'AETHERIS', url: BASE_URL }];
    let acc = '';
    segments.forEach((seg) => {
      acc += `/${seg}`;
      crumbs.push({ name: seg.charAt(0).toUpperCase() + seg.slice(1), url: `${BASE_URL}${acc}` });
    });
    const breadcrumbLd = crumbs.length > 1 ? {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.map((c, i) => ({
        '@type': 'ListItem', position: i + 1, name: c.name, item: c.url,
      })),
    } : null;

    [orgLd, breadcrumbLd, ...ldArr].filter(Boolean).forEach((obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.dataset.jsonld = 'site';
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [title, description, url, img, lang, type, path, JSON.stringify(jsonLd)]);

  return null;
};
